import { useEffect, useState, useCallback, useRef } from 'react';
import uniqBy from 'lodash/uniqBy';
import { useLazyQuery } from '@apollo/react-hooks';
import type { ApolloError, WatchQueryFetchPolicy, ErrorPolicy } from 'apollo-client';
import type { DocumentNode } from 'graphql';
import type { GenericPaginationVariables } from '../../common/types.tsx';
import type { AGGCompliantList, AGGCompliantAPI } from './types.tsx';
import { type UseFetchMoreOptions, useFetchMore, getAGGCompliantData } from './utils.tsx';

export type UsePaginationReturn = {
	hasNextPage: boolean;
	endCursor: string;
	isLoadingNextPage: boolean;
	next: (after: string) => void;
};

type BasePaginationOptions<TVariables> = {
	variables: TVariables;
	query: DocumentNode;
	onError: (arg1: ApolloError) => void;
	errorPolicy?: ErrorPolicy;
};

export type UseJqlPaginationOptions<TVariables, TItems> = BasePaginationOptions<TVariables> & {
	onCompleted: (arg1: TItems) => void;
	onPaginationError: (arg1: ApolloError) => void;
	onNextPageLoaded?: () => void;
	fetchPolicy?: WatchQueryFetchPolicy;
};

type UseAdvancedPaginationOptions<TVariables, TItems> = UseJqlPaginationOptions<
	TVariables,
	TItems
> & {
	uniqueId: string;
	filter: keyof TVariables;
};

export type UseIssuePaginationOptions<TData, TVariables> = BasePaginationOptions<TVariables> & {
	data: TData | undefined;
	listName: keyof AGGCompliantList;
};

type UsePaginationOptions<TData, TVariables> = UseIssuePaginationOptions<TData, TVariables> & {
	uniqueId: string;
	filter: keyof TVariables;
};

const paginationOptionsForIssuesByJql = {
	uniqueId: 'issueId',
	filter: 'jql',
} as const;

export function usePagination<
	TData extends AGGCompliantAPI,
	TNode,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	TVariables extends Record<PropertyKey, any>,
>({
	data,
	variables,
	listName,
	query,
	onError,
	errorPolicy = 'none',
	uniqueId,
	filter,
}: UsePaginationOptions<TData, TVariables>): UsePaginationReturn {
	const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);

	const _data = data && getAGGCompliantData<TData>(data, listName, onError);

	const hasNextPage = _data?.pageInfo?.hasNextPage || false;
	const endCursor = _data?.pageInfo?.endCursor || '';

	const fetchMore = useFetchMore<TData, TVariables>({ query, variables });

	/**
	 * creating a useRef for onError so that we don't regenerate the `next` function every time `onError` changes.
	 * - This will make the `next` reference to be more resistant to reference changes in case consumer inline the `onError` function
	 * - Moreover, `onError` is not affecting the data that we fetch, since it will be only called in in-need basis
	 * - Additionally in apollo repository, `onError` is also excluded from memoization list  (check the useQuery)
	 */
	const onErrorRef = useRef<UsePaginationOptions<TData, TVariables>['onError']>();
	onErrorRef.current = onError;

	const resetWhenChanged = filter ? variables[filter] : variables;
	useEffect(() => {
		// reset next page state
		setIsLoadingNextPage(false);
	}, [resetWhenChanged]);

	const updateQueryAGGCompliant = useCallback<
		UseFetchMoreOptions<TData, TVariables>['updateQuery']
	>(
		// @ts-expect-error TS2345 - Argument of type '(prev: TData, { fetchMoreResult }: { fetchMoreResult?: TData | undefined; variables: TVariables; }) => TData | { jira: { [x: string]: any; }; }' is not assignable to parameter of type '(prev: TData, options: { fetchMoreResult?: TData | undefined; variables: TVariables; }) => TData'.
		(prev, { fetchMoreResult }) => {
			setIsLoadingNextPage(false);
			if (!fetchMoreResult) return prev;

			const edges = [
				...(getAGGCompliantData<TData>(prev, listName)?.edges || []),
				...(getAGGCompliantData<TData>(fetchMoreResult, listName)?.edges || []),
			];

			return {
				jira: {
					...fetchMoreResult.jira,
					[listName]: {
						...getAGGCompliantData<TData>(fetchMoreResult, listName),
						edges: uniqBy(
							edges,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(edge) => edge?.node && (edge.node as TNode)[uniqueId as keyof TNode],
						),
					},
				},
			};
		},
		[setIsLoadingNextPage, listName, uniqueId],
	);

	const next = useCallback(
		(after: string) => {
			setIsLoadingNextPage(true);
			fetchMore({
				after,
				onError: onErrorRef.current,
				errorPolicy,
				updateQuery: updateQueryAGGCompliant,
			});
		},
		[fetchMore, updateQueryAGGCompliant, errorPolicy],
	);

	return {
		hasNextPage,
		endCursor,
		isLoadingNextPage,
		next,
	};
}

// Simple pagination set up to work for issues filtered by jql
export const useIssuePagination = <
	TData extends AGGCompliantAPI,
	TNode,
	TVariables extends Readonly<{
		jql: string;
	}>,
>(
	options: UseIssuePaginationOptions<TData, TVariables>,
): UsePaginationReturn =>
	usePagination<TData, TNode, TVariables>({
		...options,
		...paginationOptionsForIssuesByJql,
	});

// Advanced pagination with helper functions to loadFirstPage, loadNextPage, etc.
export const useAdvancedPagination = <
	TData extends AGGCompliantAPI,
	TNode,
	TVariables extends GenericPaginationVariables & {
		fieldIds: ReadonlyArray<string | null | undefined>;
	},
>({
	variables,
	query,
	onCompleted,
	onError,
	onPaginationError,
	onNextPageLoaded,
	uniqueId,
	filter,
	fetchPolicy,
	errorPolicy = 'none',
}: UseAdvancedPaginationOptions<TVariables, TData>) => {
	const [inFlightPage, setInFlightPage] = useState<string | null>();

	const resetWhenChanged = filter ? variables[filter] : variables;
	useEffect(() => {
		// reset inflight track
		setInFlightPage(null);
	}, [resetWhenChanged]);

	const [loadFirstPage, { data, loading, error, refetch }] = useLazyQuery<TData, TVariables>(
		query,
		{
			variables,
			errorPolicy,
			fetchPolicy: fetchPolicy || 'network-only',
			onCompleted,
			onError,
		},
	);
	if (data?.jira?.list?.__typename === 'QueryError') {
		onError && onError(data?.jira?.list);
	}
	const { next, hasNextPage, endCursor, isLoadingNextPage } = usePagination<
		TData,
		TNode,
		TVariables
	>({
		data,
		variables,
		listName: 'list',
		onError: onPaginationError,
		errorPolicy,
		query,
		uniqueId,
		filter,
	});

	const loadNextPage = useCallback(() => {
		/*
		 * Do not load the same page twice and do not load the next page if
		 * a request is already in progress.
		 */
		if (!hasNextPage || endCursor === inFlightPage || loading) {
			return;
		}
		setInFlightPage(endCursor);
		next(endCursor);
		onNextPageLoaded && onNextPageLoaded();
	}, [endCursor, hasNextPage, inFlightPage, next, onNextPageLoaded, loading]);

	return {
		loading,
		error,
		data,
		loadFirstPage,
		hasNextPage,
		isLoadingNextPage,
		loadNextPage,
		refetch,
	};
};

export const useJqlPagination = <
	TData extends AGGCompliantAPI,
	TNode,
	TVariables extends Readonly<
		GenericPaginationVariables & {
			jql: string;
			fieldIds: ReadonlyArray<string | null | undefined>;
		}
	>,
>(
	options: UseJqlPaginationOptions<TVariables, TData>,
) =>
	useAdvancedPagination<TData, TNode, TVariables>({
		...options,
		...paginationOptionsForIssuesByJql,
	});

export const useRefreshInterval = (callback: () => void, condition: boolean, interval?: number) => {
	useEffect(() => {
		if (interval == null) {
			// Replace with lodash/noop
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			return () => {};
		}

		const timer = setTimeout(() => {
			if (condition) {
				callback();
			}
		}, interval * 1000);

		return () => {
			clearTimeout(timer);
		};
	}, [interval, callback, condition]);
};
